import { Eye, UserCheck, Users, UsersRound } from "lucide-react";
import { ContainerCard, EmployeeDetails, ModuleTitleCard } from "../../components/ui/cards";
import { Reactable } from "../../components/ui/reactable";
import { SUBORDINATES_URL } from "../../constants/api";
import { useMemo, useState } from "react";
import { Button } from "../../components/ui/button";
import { useNavigate } from "react-router-dom";
import { cn } from "../../utils/tailwindUtils";
import dayjs from "dayjs";

const SubordinatesList = () => {

    let navigate = useNavigate();

    // Admins / super-admins see the whole company by default; the toggle lets
    // them narrow to their own team (the Subordinates hierarchy configured at
    // /configuration/subordinate/). Non-admins are always team-scoped, so the
    // toggle is hidden for them.
    const isAdmin =
        JSON.parse(sessionStorage.getItem("is_admin") || "false") ||
        JSON.parse(sessionStorage.getItem("is_super_admin") || "false");

    // STATES
    const [data, setData] = useState([])
    const [totalRecords, setTotalRecords] = useState(0)
    const [scope, setScope] = useState("all"); // 'all' | 'team'

    // Only send scope=team to the backend; "all" is the default company view.
    const tableFilter = useMemo(
        () => (scope === "team" ? { scope: "team" } : {}),
        [scope]
    );

    const columns = [
        {
            accessor: "employee",
            Header: "Employee",
            sortable: true,
            fixed: true,
            filterable: true,
            width: "250px",
            Cell: ({ row }) => {
                const record = row;
                let empDetails = {
                    ...record?.employee,
                    designation_id: record?.employee?.designation
                        ? {
                            id: record?.employee.designation.id,
                            designation_name: record?.employee.designation.name,
                        }
                        : { id: 0, designation_name: "" },
                    first_name: record?.employee?.employee_name || "-",
                    id: record?.employee?.employee_id || "-",
                };

                return <EmployeeDetails details={empDetails} size="xs" />;
            },
        },
        {
            accessor: "department",
            Header: "Department",
            filterable: true,
        },
        {
            accessor: "date_of_joining",
            Header: "Date of joining",
            filterable: true,
            filterType: 'date',
            Cell: ({ value }) => {
                if (!value) return "-";

                try {
                    return dayjs(value, "YYYY-MM-DD").format("DD-MM-YYYY");
                } catch (error) {
                    console.error("Date formatting error:", error);
                    return value;
                }
            }
        },
        {
            accessor: "employee_group",
            Header: "Employee Group",
        },
        {
            accessor: "email_id",
            Header: "Email Id",
        },
        {
            accessor: 'actions',
            Header: 'Actions',
            fixed: true,
            align: 'right',
            Cell: ({ row }) => {                
                const handleView = () => {    
                    navigate("/employee/subordinate/viewDetails", {
                        state: {
                            row: row,
                            subordinateId: row.subordinate_id,
                            employeeID: row.employee.id
                        }
                    });
                };

                return (
                    <>
                        <Button
                            variant='ghost'
                            onClick={handleView}
                            className="px-3 py-2 hover:bg-orange-50 dark:hover:bg-orange-900/20"
                            title="View Employee Details"
                        >
                            <Eye className="text-orange-500 h-4 w-4" />
                        </Button>
                    </>
                );
            }
        }
    ]

    return (
        <ContainerCard>
            {/* title */}
            <ModuleTitleCard Icon={UserCheck} title={'Subordinates'} subtitle={'Subordinates details and there attendance summary'} totalRecords={totalRecords} />

            {/* My Team / All toggle — admins & super-admins only */}
            {isAdmin && (
                <div className="px-3 mb-2">
                    <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 p-1">
                        <button
                            onClick={() => setScope("team")}
                            className={cn(
                                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                                scope === "team"
                                    ? "bg-sidebar text-white shadow-sm"
                                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            )}
                        >
                            <Users className="h-3.5 w-3.5" /> My Team
                        </button>
                        <button
                            onClick={() => setScope("all")}
                            className={cn(
                                "flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors",
                                scope === "all"
                                    ? "bg-sidebar text-white shadow-sm"
                                    : "text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700"
                            )}
                        >
                            <UsersRound className="h-3.5 w-3.5" /> All Users
                        </button>
                    </div>
                </div>
            )}

            {/* table */}
            <Reactable
                columns={columns}
                data={data}
                setData={setData}
                setTotalCount={setTotalRecords}
                serverSide={true}
                url={SUBORDINATES_URL}
                filter={tableFilter}
                tableHeight={!isAdmin ? "calc(100vh - 320px)" : "calc(100vh - 380px)"}
                rowSize={"sm"}
                isCSVRequired
                isPDFRequired
            />
        </ContainerCard>
    );
}

export default SubordinatesList;